import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../components/ui/dialog';
import { ArrowLeft, Network, Star, UserPlus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { clientGroupsAPI } from '../lib/clientGroupsAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { GrantPromptDialog } from '../components/portal/GrantPromptDialog';

// Relationship types a member can have to the primary (excludes 'primary' — that's the root).
const RELATIONSHIP_TYPES = [
  'spouse', 'company', 'trust', 'partnership', 'smsf', 'director', 'shareholder',
  'trustee', 'beneficiary', 'member', 'sole_trader', 'related', 'other',
];

const relColor = (t) => ({
  primary: 'bg-primary text-white',
  spouse: 'bg-pink-100 text-pink-800',
  company: 'bg-blue-100 text-blue-800',
  trust: 'bg-indigo-100 text-indigo-800',
  smsf: 'bg-accent text-accent-foreground',
  partnership: 'bg-amber-100 text-amber-800',
}[t] || 'bg-muted text-foreground');

export default function ClientGroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(undefined);

  // Add-member dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [promptClient, setPromptClient] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [relType, setRelType] = useState('spouse');
  const [submitting, setSubmitting] = useState(false);

  // Rename state
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const canManage = ['owner', 'admin', 'accountant', 'bookkeeper', 'staff'].includes(userRole);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientGroupsAPI.get(id);
      setGroup(res.data.group);
      setMembers(res.data.members || []);
    } catch (err) {
      toast.error('Failed to load client group');
      navigate('/client-groups');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    api.get('/api/me').then((res) => setUserRole(res.data.active_membership?.role)).catch(() => {});
    load();
  }, [load]);

  const openAddMember = async () => {
    try {
      const res = await clientsAPI.list();
      const all = res.data.clients || res.data || [];
      // Clients not already in a group (eligible to link).
      setUnassigned(all.filter((c) => !c.client_group_id));
      setSelectedClient('');
      setRelType('spouse');
      setAddOpen(true);
    } catch {
      toast.error('Failed to load clients');
    }
  };

  const handleAddMember = async () => {
    if (!selectedClient) { toast.error('Select a client to link'); return; }
    setSubmitting(true);
    try {
      await clientGroupsAPI.addMember(id, { client_id: selectedClient, relationship_type: relType });
      toast.success('Member linked to group');
      setAddOpen(false);
      setPromptClient(selectedClient);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (clientId) => {
    try {
      await clientGroupsAPI.removeMember(id, clientId);
      toast.success('Member removed from group');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleSetPrimary = async (clientId) => {
    try {
      await clientGroupsAPI.update(id, { primary_client_id: clientId });
      toast.success('Primary client updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set primary');
    }
  };

  const handleRename = async () => {
    try {
      await clientGroupsAPI.update(id, { group_name: nameDraft });
      toast.success('Group renamed');
      setEditingName(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    }
  };

  if (loading || !group) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <GrantPromptDialog open={!!promptClient} clientId={promptClient} onClose={() => setPromptClient(null)} />
      <Button variant="ghost" onClick={() => navigate('/client-groups')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> All Client Groups
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Network className="w-6 h-6 text-foreground" />
          </div>
          <div>
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} className="w-72" />
                <Button size="sm" onClick={handleRename}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Cancel</Button>
              </div>
            ) : (
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {group.group_name}
                {canManage && (
                  <button onClick={() => { setNameDraft(group.group_name); setEditingName(true); }} className="text-muted-foreground/70 hover:text-muted-foreground">
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
              </h1>
            )}
            <p className="text-muted-foreground text-sm mt-1 capitalize">{group.group_type} group · {members.length} member(s)</p>
          </div>
        </div>
        {canManage && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button onClick={openAddMember} data-testid="add-member-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Link Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Link a client into this group</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground/90">Client</label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger data-testid="member-client-select"><SelectValue placeholder="Select a client…" /></SelectTrigger>
                    <SelectContent>
                      {unassigned.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground/70">No ungrouped clients available</div>
                      ) : unassigned.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.display_name} ({c.entity_type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground/90">Relationship to primary</label>
                  <Select value={relType} onValueChange={setRelType}>
                    <SelectTrigger data-testid="member-rel-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RELATIONSHIP_TYPES.map((t) => (
                        <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={handleAddMember} disabled={submitting}>Link Member</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Members */}
      <Card>
        <CardHeader><CardTitle className="text-lg text-foreground">Group Members</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">Entity Type</TableHead>
                <TableHead className="font-semibold">Relationship</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const isPrimary = m.id === group.primary_client_id;
                return (
                  <TableRow key={m.id} className="hover:bg-muted/50" data-testid={`member-row-${m.id}`}>
                    <TableCell>
                      <button className="font-medium text-foreground hover:underline flex items-center gap-1"
                        onClick={() => navigate(`/clients/${m.id}`)}>
                        {isPrimary && <Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
                        {m.display_name}
                      </button>
                    </TableCell>
                    <TableCell className="capitalize text-sm text-muted-foreground">{(m.entity_type || '').replace('_', ' ')}</TableCell>
                    <TableCell><Badge className={relColor(m.relationship_type)}>{(m.relationship_type || 'related').replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="capitalize text-sm text-muted-foreground">{(m.status || '').replace('_', ' ')}</TableCell>
                    <TableCell className="text-right">
                      {canManage && !isPrimary && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" title="Make primary" onClick={() => handleSetPrimary(m.id)}>
                            <Star className="w-4 h-4 text-amber-500" />
                          </Button>
                          <Button size="sm" variant="ghost" title="Remove from group" onClick={() => handleRemoveMember(m.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
